import { useEffect, useState } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Badge,
  Button,
  Checkbox,
  Creatable,
  Input,
  InputImage,
  Select,
} from '@iveco-ti/iveco-ds'
import { FormProvider, useForm } from '@iveco-ti/iveco-ds/hook-form'
import { Mail } from 'lucide-react'
import { HeadProvider, Title } from 'react-head'
import { useTranslation } from 'react-i18next'

import { GraphicArea } from '@/common/components/GraphicArea'
import { useGetUserInfo } from '@/common/hooks/useGetUserInfo'
import { formatDate } from '@/common/utils/formatDate'
import { PreferencesFormSkeleton } from './components/PreferencesFormSkeleton'
import { useEditAvatar } from './hooks/useEditAvatar'
import { useEditUserPreferences } from './hooks/useEditUserPreferences'
import {
  ProfilePreferencesForm,
  ProfilePreferencesSchema,
} from './schemas/ProfilePreferencesSchema'

export default function Preferences() {
  const [isEditing, setIsEditing] = useState(false)

  const { t } = useTranslation(['profile', 'common'])
  const { handleEditUserPreferences, isPendingEditUserPreferences } =
    useEditUserPreferences()
  const { handleEditAvatar, isPendingEditAvatar } = useEditAvatar()
  const hookForm = useForm<ProfilePreferencesForm>({
    resolver: zodResolver(ProfilePreferencesSchema()),
  })
  const { userInfo, isPendingUserInfo } = useGetUserInfo()

  const onSubmit = async (data: ProfilePreferencesForm) => {
    if (!userInfo) return

    if (data.avatarFile && data.avatarFile instanceof File) {
      await handleEditAvatar(data.avatarFile)
    }

    const payload = {
      userId: userInfo.id,
      displayName: data.displayName,
      locale: data.language,
      allowedPosts: userInfo.UserPreference?.allowedPosts,
      allowedApplications: userInfo.UserPreference?.allowedApplications || [],
      registeredApplications:
        userInfo.UserPreference?.registeredApplications || [],
      isRoot: userInfo.UserPreference?.isRoot || false,
    }

    await handleEditUserPreferences(payload)

    setIsEditing(false)
  }

  const LANGUAGE_OPTIONS = [
    { label: t('preferences.language.options.pt_br'), value: 'pt-BR' },
    { label: t('preferences.language.options.en'), value: 'en' },
    { label: t('preferences.language.options.es'), value: 'es' },
  ]

  useEffect(() => {
    if (userInfo?.UserPreference) {
      hookForm.reset({
        avatarFile: userInfo.UserPreference?.avatarUrl
          ? `${userInfo.UserPreference.avatarUrl}?v=${Date.now()}`
          : undefined,
        displayName: userInfo.UserPreference?.displayName,
        language: userInfo.UserPreference?.locale,
        email: userInfo.Person?.email,
        allowedPosts: userInfo.UserPreference?.allowedPosts,
        allowedApplications:
          userInfo.UserPreference?.allowedApplications.map((app) => ({
            label: app,
            value: app,
          })) || [],
        registeredApplications:
          userInfo.UserPreference?.registeredApplications.map((app) => ({
            label: app,
            value: app,
          })) || [],
      })
    }
  }, [userInfo, hookForm])

  if (isPendingUserInfo) {
    return <PreferencesFormSkeleton />
  }

  return (
    <>
      <HeadProvider>
        <Title>{t('profile:preferences.browser.title')}</Title>
      </HeadProvider>

      <GraphicArea.Root>
        <FormProvider {...hookForm}>
          <GraphicArea.Header className="flex items-center justify-between gap-4 mr-0">
            <h3 className="text-2xl font-semibold text-brand-800">
              {t('preferences.title')}
            </h3>

            {userInfo?.UserPreference?.isRoot && (
              <Badge.Root>{t('common:badges.root_user')}</Badge.Root>
            )}
          </GraphicArea.Header>

          <GraphicArea.Body>
            <form className="flex flex-col gap-6 justify-between">
              <div className="max-h-full overflow-y-auto">
                <div className="flex flex-col sm:flex-row w-full justify-start gap-6">
                  <div className="flex flex-col gap-2">
                    <InputImage
                      accept="image/jpeg,image/jpg,image/png"
                      size="md"
                      disabled={!isEditing}
                      label={t('preferences.avatar.label')}
                      name="avatarFile"
                      helperText={t('preferences.avatar.helper_text')}
                      text={t('preferences.avatar.text')}
                    />
                  </div>

                  <div className="flex flex-col gap-5 w-full">
                    <Input.Base
                      disabled={!isEditing}
                      name="displayName"
                      label={t('preferences.display_name.label')}
                      placeholder={t('preferences.display_name.placeholder')}
                    />

                    <Select.Single
                      disabled={!isEditing}
                      options={LANGUAGE_OPTIONS}
                      name="language"
                      label={t('preferences.language.label')}
                      placeholder={t('preferences.language.placeholder')}
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-5 mt-5">
                  <div className="flex flex-col sm:flex-row w-full gap-5">
                    <div className="w-full sm:w-1/2">
                      <Input.Icon
                        icon={<Mail />}
                        name="email"
                        disabled
                        label={t('preferences.email.label')}
                        placeholder={t('preferences.email.placeholder')}
                      />
                    </div>

                    <div className="w-full sm:w-1/2">
                      <div className="flex flex-col">
                        <label className="block text-sm font-normal leading-6 text-gray-900 mb-2">
                          {t('preferences.allowed_posts.label')}
                        </label>
                        <div className="p-3 border border-gray-200 rounded-md bg-white h-[42px] flex items-center">
                          <Checkbox
                            disabled
                            name="allowedPosts"
                            text={
                              hookForm.watch('allowedPosts')
                                ? t('preferences.allowed_posts.authorized')
                                : t('preferences.allowed_posts.not_authorized')
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row w-full gap-5">
                    <div className="w-full sm:w-1/2">
                      <Creatable.Sync
                        isDisabled
                        isMulti
                        name="allowedApplications"
                        label={t('preferences.allowed_applications.label')}
                        placeholder={t(
                          'preferences.allowed_applications.placeholder',
                        )}
                        icon={null}
                        isClearable={false}
                      />
                    </div>

                    <div className="w-full sm:w-1/2">
                      <Creatable.Sync
                        isDisabled
                        isMulti
                        name="registeredApplications"
                        label={t('preferences.registered_applications.label')}
                        placeholder={t(
                          'preferences.registered_applications.placeholder',
                        )}
                        icon={null}
                        isClearable={false}
                      />
                    </div>
                  </div>

                  {!isEditing &&
                    userInfo?.UserPreference?.termOfUseAcceptedDate && (
                      <div className="mt-3">
                        <span className="font-inter font-light text-sm text-gray-500">
                          {userInfo.UserPreference?.termOfUseAcceptedIP
                            ? t('preferences.terms_accepted.text', {
                                date:
                                  formatDate(
                                    new Date(
                                      userInfo.UserPreference.termOfUseAcceptedDate,
                                    ),
                                    'dd/MM/yyyy HH:mm',
                                  ) || '',
                                ip: userInfo.UserPreference.termOfUseAcceptedIP,
                              })
                            : t('preferences.terms_accepted.text_without_ip', {
                                date:
                                  formatDate(
                                    new Date(
                                      userInfo.UserPreference.termOfUseAcceptedDate,
                                    ),
                                    'dd/MM/yyyy HH:mm',
                                  ) || '',
                              })}
                        </span>
                      </div>
                    )}
                </div>
              </div>
            </form>
          </GraphicArea.Body>

          <GraphicArea.Footer className="flex flex-row justify-end">
            {isEditing && (
              <div className="flex flex-row w-full justify-end gap-2">
                <Button.Root
                  type="button"
                  disabled={isPendingEditUserPreferences || isPendingEditAvatar}
                  variant="secondary"
                  onClick={() => {
                    setIsEditing(!isEditing)
                    hookForm.reset()
                  }}
                >
                  {t('actions.cancel_button.text')}
                </Button.Root>

                <Button.Root onClick={hookForm.handleSubmit(onSubmit)}>
                  {t('actions.save_button.text')}
                </Button.Root>
              </div>
            )}

            {!isEditing && (
              <Button.Root
                type="button"
                onClick={() => setIsEditing(!isEditing)}
              >
                {t('actions.edit_button.text')}
              </Button.Root>
            )}
          </GraphicArea.Footer>
        </FormProvider>
      </GraphicArea.Root>
    </>
  )
}
